import { supabase } from './supabase';
import { hybridCache } from './hybridCache';
import { createLogger } from './logger';

const logger = createLogger({ module: 'BackgroundSync', level: 'info' });

interface SyncOperation {
  id: string;
  type: 'create' | 'update' | 'delete';
  table: string;
  data: any;
  userId: string;
  timestamp: number;
  retryCount: number;
  priority: number; // Higher number = higher priority
}

interface ConflictResolution {
  strategy: 'client_wins' | 'server_wins' | 'merge' | 'manual';
  mergeFields?: string[];
}

export class BackgroundSyncManager {
  private syncQueue: SyncOperation[] = [];
  private isOnline = navigator.onLine;
  private syncInterval: ReturnType<typeof setTimeout> | null = null;
  private isProcessing = false;
  private conflictResolutions = new Map<string, ConflictResolution>();
  
  constructor() {
    logger.info('Background sync manager initialized');
    this.setupEventListeners();
    this.startSyncProcess();
    this.registerDefaultConflictResolutions();
  }
  
  queueOperation(operation: Omit<SyncOperation, 'id' | 'timestamp' | 'retryCount' | 'priority'>): void {
    const syncOp: SyncOperation = {
      ...operation,
      id: crypto.randomUUID(),
      timestamp: Date.now(),
      retryCount: 0,
      priority: this.getOperationPriority(operation.type, operation.table)
    };
    
    // Insert in priority order
    const insertIndex = this.syncQueue.findIndex(op => op.priority < syncOp.priority);
    if (insertIndex === -1) {
      this.syncQueue.push(syncOp);
    } else {
      this.syncQueue.splice(insertIndex, 0, syncOp);
    }
    
    logger.debug('Operation queued for sync', { 
      id: syncOp.id, 
      type: syncOp.type, 
      table: syncOp.table,
      priority: syncOp.priority,
      queueLength: this.syncQueue.length
    });
    
    // Try immediate sync if online
    if (this.isOnline && !this.isProcessing) {
      this.processSyncQueue();
    }
  }
  
  private async processSyncQueue(): Promise<void> {
    if (!this.isOnline || this.syncQueue.length === 0 || this.isProcessing) return;
    
    this.isProcessing = true;
    logger.debug('Processing sync queue', { queueLength: this.syncQueue.length });
    
    const operations = [...this.syncQueue];
    this.syncQueue = [];
    
    for (const operation of operations) {
      try {
        await this.executeOperation(operation);
        
        // Update cache after successful operation
        await this.updateCacheAfterSync(operation);
        
        logger.debug('Sync operation completed', { 
          id: operation.id, 
          type: operation.type, 
          table: operation.table 
        });
        
      } catch (error) {
        logger.warn('Sync operation failed', { 
          id: operation.id, 
          error: error instanceof Error ? error.message : 'Unknown error',
          retryCount: operation.retryCount
        });
        
        // Retry logic with exponential backoff
        if (operation.retryCount < 3) {
          operation.retryCount++;
          operation.priority = Math.max(1, operation.priority - 1); // Lower priority on retry
          
          // Add back to queue with delay
          setTimeout(() => {
            this.syncQueue.push(operation);
          }, Math.pow(2, operation.retryCount) * 1000); // 2s, 4s, 8s delays
        } else {
          logger.error('Sync operation failed permanently', { 
            id: operation.id, 
            type: operation.type, 
            table: operation.table 
          });
          
          // Store failed operation for manual resolution
          await this.storeFailedOperation(operation, error);
        }
      }
    }
    
    this.isProcessing = false;
  }
  
  private async executeOperation(operation: SyncOperation): Promise<void> {
    const { type, table, data, userId } = operation;
    
    // Check for conflicts before executing
    if (type === 'update') {
      const conflict = await this.detectConflict(operation);
      if (conflict) {
        await this.resolveConflict(operation, conflict);
        return;
      }
    }
    
    switch (type) {
      case 'create':
        const { error: createError } = await supabase
          .from(table)
          .insert(data);
        if (createError) throw createError;
        break;
        
      case 'update':
        const { error: updateError } = await supabase
          .from(table)
          .update(data)
          .eq('id', data.id)
          .eq('user_id', userId);
        if (updateError) throw updateError;
        break;
        
      case 'delete':
        const { error: deleteError } = await supabase
          .from(table)
          .delete()
          .eq('id', data.id)
          .eq('user_id', userId);
        if (deleteError) throw deleteError;
        break;
    }
  }
  
  private async detectConflict(operation: SyncOperation): Promise<any | null> {
    try {
      const { data: serverData, error } = await supabase
        .from(operation.table)
        .select('*')
        .eq('id', operation.data.id)
        .eq('user_id', operation.userId)
        .single();
      
      if (error || !serverData) return null;
      
      // Check if server version is newer than our operation
      const serverTimestamp = new Date(serverData.updated_at || serverData.created_at).getTime();
      const operationTimestamp = operation.timestamp;
      
      if (serverTimestamp > operationTimestamp) {
        return serverData;
      }
      
      return null;
    } catch (error) {
      logger.warn('Error detecting conflict', { error });
      return null;
    }
  }
  
  private async resolveConflict(operation: SyncOperation, serverData: any): Promise<void> {
    const resolution = this.conflictResolutions.get(operation.table) || 
                      { strategy: 'server_wins' };
    
    logger.info('Resolving conflict', { 
      table: operation.table, 
      strategy: resolution.strategy,
      operationId: operation.id
    });
    
    switch (resolution.strategy) {
      case 'client_wins':
        // Proceed with original operation
        await this.forceExecuteOperation(operation);
        break;
        
      case 'server_wins':
        // Update local cache with server data
        await this.updateLocalData(operation.table, serverData, operation.userId);
        break;
        
      case 'merge':
        // Merge client and server data
        const mergedData = await this.mergeData(operation.data, serverData, resolution.mergeFields);
        const mergedOperation = { ...operation, data: mergedData };
        await this.forceExecuteOperation(mergedOperation);
        break;
        
      case 'manual':
        // Store for manual resolution
        await this.storeConflictForManualResolution(operation, serverData);
        break;
    }
  }
  
  private async forceExecuteOperation(operation: SyncOperation): Promise<void> {
    // Execute operation without conflict checking
    const { type, table, data, userId } = operation;
    
    switch (type) {
      case 'update':
        const { error } = await supabase
          .from(table)
          .update(data)
          .eq('id', data.id)
          .eq('user_id', userId);
        if (error) throw error;
        break;
    }
  }
  
  private async mergeData(clientData: any, serverData: any, mergeFields?: string[]): Promise<any> {
    if (!mergeFields) {
      // Default merge: client data takes precedence for most fields
      return {
        ...serverData,
        ...clientData,
        updated_at: new Date().toISOString()
      };
    }
    
    // Merge only specified fields
    const merged = { ...serverData };
    for (const field of mergeFields) {
      if (clientData[field] !== undefined) {
        merged[field] = clientData[field];
      }
    }
    merged.updated_at = new Date().toISOString();
    
    return merged;
  }
  
  private async updateLocalData(table: string, data: any, userId: string): Promise<void> {
    // Update local cache with server data
    const cacheKeys = this.getCacheKeysForTable(table);
    
    for (const key of cacheKeys) {
      await hybridCache.invalidate(key, userId);
    }
  }
  
  private async updateCacheAfterSync(operation: SyncOperation): Promise<void> {
    // Invalidate related cache entries
    const cacheKeys = this.getCacheKeysForTable(operation.table);
    
    await Promise.allSettled(
      cacheKeys.map(key => 
        hybridCache.invalidate(key, operation.userId)
      )
    );
  }
  
  private getCacheKeysForTable(table: string): string[] {
    // Map table names to cache keys that should be invalidated
    const tableKeyMap: Record<string, string[]> = {
      'notes': ['user-unified-content', 'notes', 'timeline-data'],
      'career_cards': ['user-unified-content', 'career-cards', 'timeline-data'],
      'reviews': ['user-unified-content', 'reviews'],
      'files': ['files', 'storage-quota'],
      'user_settings': ['user-settings']
    };
    
    return tableKeyMap[table] || [];
  }
  
  private getOperationPriority(type: string, table: string): number {
    // Higher priority for user-facing operations
    const typePriority = {
      'delete': 10,
      'create': 8,
      'update': 6
    };
    
    const tablePriority = {
      'user_settings': 9,
      'notes': 7,
      'career_cards': 7,
      'reviews': 6,
      'files': 5
    };
    
    return (typePriority[type as keyof typeof typePriority] || 5) + 
           (tablePriority[table as keyof typeof tablePriority] || 3);
  }
  
  private registerDefaultConflictResolutions(): void {
    // Default conflict resolution strategies
    this.conflictResolutions.set('notes', { 
      strategy: 'merge', 
      mergeFields: ['title', 'content', 'custom_tags', 'career_impact', 'engagement_level'] 
    });
    
    this.conflictResolutions.set('career_cards', { 
      strategy: 'merge', 
      mergeFields: ['card_data'] 
    });
    
    this.conflictResolutions.set('reviews', { 
      strategy: 'client_wins' 
    });
    
    this.conflictResolutions.set('user_settings', { 
      strategy: 'merge' 
    });
  }
  
  private async storeFailedOperation(operation: SyncOperation, error: any): Promise<void> {
    try {
      await supabase
        .from('sync_failures')
        .insert({
          operation_id: operation.id,
          user_id: operation.userId,
          table_name: operation.table,
          operation_type: operation.type,
          operation_data: operation.data,
          error_message: error instanceof Error ? error.message : 'Unknown error',
          created_at: new Date().toISOString()
        });
    } catch (insertError) {
      logger.error('Failed to store failed operation', { insertError });
    }
  }
  
  private async storeConflictForManualResolution(operation: SyncOperation, serverData: any): Promise<void> {
    try {
      await supabase
        .from('sync_conflicts')
        .insert({
          operation_id: operation.id,
          user_id: operation.userId,
          table_name: operation.table,
          client_data: operation.data,
          server_data: serverData,
          created_at: new Date().toISOString(),
          status: 'pending'
        });
    } catch (insertError) {
      logger.error('Failed to store conflict', { insertError });
    }
  }
  
  private setupEventListeners(): void {
    window.addEventListener('online', () => {
      logger.info('Connection restored, processing sync queue');
      this.isOnline = true;
      this.processSyncQueue();
    });
    
    window.addEventListener('offline', () => {
      logger.info('Connection lost, queuing operations for later sync');
      this.isOnline = false;
    });
    
    // Listen for visibility changes to sync when app becomes active
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && this.isOnline && this.syncQueue.length > 0) {
        this.processSyncQueue();
      }
    });
  }
  
  private startSyncProcess(): void {
    // Process sync queue every 30 seconds
    this.syncInterval = setInterval(() => {
      if (this.isOnline && this.syncQueue.length > 0) {
        this.processSyncQueue();
      }
    }, 30000);
  }
  
  // Public methods for managing sync
  getSyncStatus(): {
    isOnline: boolean;
    queueLength: number;
    isProcessing: boolean;
    lastSyncTime: number | null;
  } {
    return {
      isOnline: this.isOnline,
      queueLength: this.syncQueue.length,
      isProcessing: this.isProcessing,
      lastSyncTime: null // Could track this if needed
    };
  }
  
  async forceSyncNow(): Promise<void> {
    if (this.isOnline) {
      await this.processSyncQueue();
    }
  }
  
  clearQueue(): void {
    this.syncQueue = [];
    logger.info('Sync queue cleared');
  }
  
  destroy(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
    
    window.removeEventListener('online', this.processSyncQueue);
    window.removeEventListener('offline', () => {});
    document.removeEventListener('visibilitychange', this.processSyncQueue);
    
    logger.info('Background sync manager destroyed');
  }
}

// Create singleton instance
export const backgroundSyncManager = new BackgroundSyncManager(); 